import get from 'lodash.get';
import checkWithinPolygon from 'point-in-geopolygon';
import { STATUS_INACTIVE } from '../InfoWindow/infoWindow';

/**
 * Check whether or not a new dynamic spatial already exists for appropriate
 * handling using the `source`
 * @param {Array} dynamicLayers - List of existing layers
 * @param {Object} incomingSpatialObject - The `message.body` from Hermes
 */
export const getExistingDynamicLayer = (
  dynamicLayers,
  incomingSpatialObject,
) => {
  return dynamicLayers.find(dynamicLayer => {
    const isSameKey = dynamicLayer.key === incomingSpatialObject.layer.key;
    const isNegativeSpatialObject =
      `${dynamicLayer?.attributes?.display}`.toLowerCase() === 'false';
    return isSameKey && !isNegativeSpatialObject;
  });
};

export const filterDynamicLayers = (dynamicLayers = []) => {
  return dynamicLayers
    .map(dynamicLayer => {
      const layer = filterNegativeSpatialObjects(dynamicLayer);
      if (
        Array.isArray(layer?.spatial_objects) &&
        layer.spatial_objects.length === 0
      ) {
        return null;
      }

      return layer;
    })
    .filter(layer => layer !== null);
};

/**
 * Check whether or not a new dynamic spatial object already exists
 * for appropriate handling
 * @param {Array} dynamicLayers - List of existing layers
 * @param {Object} incomingSpatialObject - The `message.body` from Hermes
 */
export const getExistingDynamicObject = (
  dynamicLayers,
  incomingSpatialObject,
) => {
  return dynamicLayers.find(
    dynamicLayer =>
      dynamicLayer.key === incomingSpatialObject.layer.key &&
      dynamicLayer.spatial_objects.find(
        object => object.external_id === incomingSpatialObject.external_id,
      ),
  );
};

/**
 * Normalize all spatial layers for app usage
 * @param {Array} allLayers - all spatial layers (dynamic and static)
 */
export const normalizeAllSpatialLayers = allLayers =>
  allLayers.map(layer => {
    // If auto_display is present in the payload and its truthy, then
    // automatically mark the layer as being enabled (checked).
    let isChecked = Object.hasOwnProperty.call(layer, 'isChecked')
      ? layer.isChecked
      : false;

    if (layer.auto_display) {
      isChecked = true;
    }

    if (!isSpatialLayerDynamic(layer)) {
      return {
        ...layer,
        isChecked,
        isHovered: Object.hasOwnProperty.call(layer, 'isHovered')
          ? layer.isChecked
          : false,
        status: Object.hasOwnProperty.call(layer, 'status')
          ? layer.status
          : STATUS_INACTIVE,
      };
    }

    // Normalize `coordinates` for Google Maps usage
    const normalizedDynamicObjects = layer.spatial_objects.map(object => {
      const coordinates = get(object, 'shape.features[0].geometry.coordinates');
      return {
        ...object,
        allCoordinates: [
          {
            lat: coordinates[1],
            lng: coordinates[0],
          },
        ],
      };
    });

    return {
      ...layer,
      isChecked,
      // TODO checking layer.isHovered might be enough, ignoring lint rule for now.
      // eslint-disable-next-line no-prototype-builtins
      isHovered: layer.hasOwnProperty('isHovered') ? layer.isChecked : false,
      spatial_objects: normalizedDynamicObjects,
    };
  });

/**
 * Normalizes a dynamic spatial layer when receiving it for the first time
 * through Hermes
 * @param {Object} layer - The `message.body` from Hermes
 * @param {Object} layer.attributes
 * @param {String} layer.attributes.name
 * @param {String} layer.attributes.role
 * @param {Boolean} layer.auto_display
 * @param {String} layer.date_created
 * @param {String} layer.external_id
 * @param {Boolean} layer.is_dynamic
 * @param {Object} layer.layer
 * @param {String} layer.title
 * @param {Object} layer.layer.category
 * @param {String} layer.layer.category.key
 * @param {String} layer.layer.category.name
 * @param {String} layer.layer.category.role
 * @param {Object} layer.shape
 * @param {String} layer.shape.type
 * @param {Array} layer.shape.coordinates - [x, y] coordinates
 */
export const normalizeDynamicLayerMessage = layer => {
  const { layer: layerObj = {} } = layer;
  const { attributes = null, auto_display = false } = layerObj;

  return {
    ...layer.layer,
    attributes, // this is the display_schema
    display_attributes: null,
    id: null, // no ID unless it comes from Prague
    isChecked: auto_display ? true : false,
    isHovered: false,
    spatial_objects: [normalizeDynamicObject(layer)],
  };
};

/**
 * Normalizes a dynamic spatial object when receiving it for the first time or
 * for an existing spatial layer
 * through Hermes
 * @param {Object} layer - The `message.body` from Hermes
 * @param {Object} layer.attributes
 * @param {String} layer.attributes.name
 * @param {String} layer.attributes.role
 * @param {String} layer.attributes.unit_id
 * @param {String} layer.date_created
 * @param {String} layer.external_id
 * @param {Boolean} layer.is_dynamic
 * @param {Object} layer.layer
 * @param {String} layer.title
 * @param {Object} layer.layer.category
 * @param {String} layer.layer.category.key
 * @param {String} layer.layer.category.name
 * @param {String} layer.layer.key
 * @param {String} layer.layer.name
 * @param {String} layer.layer.source
 * @param {Object} layer.shape
 * @param {Array} layer.shape.features
 * @param {String} layer.shape.type
 */
export function normalizeDynamicObject(layer) {
  return {
    allCoordinates: [
      {
        lat: layer.shape.features[0].geometry.coordinates[1],
        lng: layer.shape.features[0].geometry.coordinates[0],
      },
    ],
    date_created: layer.date_created,
    ...layer,
  };
}

/**
 * Checks whether or not a spatial object returned from Prague or Hermes has
 * the `is_dynamic` attribute. If so, assume that the spatial layer is dynamic
 * @param {Object} layer - spatial layer from Prague or Hermes
 */
export function isSpatialLayerDynamic(layer) {
  // Starting with Axon (4.18.0), spatial layers can also be a collection of
  // spatial objects (snapshots).
  if (Array.isArray(layer?.data) && layer?.data.length > 0) {
    const firstLayer = layer?.data[0];
    const isDynamic = firstLayer?.is_dynamic === true;
    if (firstLayer?.is_dynamic === true) {
      return true;
    }
    if (
      !isDynamic &&
      Array.isArray(firstLayer.spatial_objects) &&
      firstLayer.spatial_objects.length > 0
    ) {
      const firstSpatialObject = firstLayer?.spatial_objects[0];
      if (firstSpatialObject?.is_dynamic) {
        return true;
      }
    }
    return false;
  }
  return (
    layer?.spatial_objects?.[0]?.is_dynamic === true || // Prague
    layer?.is_dynamic === true // Hermes
  );
}

/**
 * Negative spatial objects are those that have their attributes.display flag
 * set to false. This signals that the marker should be removed from the map.
 * @param {Object} spatialObject
 * @returns {Boolean} - true if the spatial object is negative
 */
export const checkNegativeSpatialObject = spatialObject => {
  const display = spatialObject?.attributes?.display;
  return `${display}`.toLowerCase() === 'false';
};

/**
 * Filters out negative spatial objects from a spatial layer
 * @param {Object} spatialLayer
 * @returns {Object} - spatial layer with negative spatial objects removed
 */
export function filterNegativeSpatialObjects(spatialLayer) {
  const filteredSpatialObjects = spatialLayer.spatial_objects.filter(
    spatialObject => !checkNegativeSpatialObject(spatialObject),
  );
  const filteredLayer = {
    ...spatialLayer,
    spatial_objects: filteredSpatialObjects,
  };
  return filteredLayer;
}

/**
 * Partitions spatial layers into two lists for dynamic and static layers
 * @param {Array} allSpatialLayers - Full list of spatial layers returned from
 * the Prague API
 */
export const partitionLayers = allSpatialLayers => {
  return allSpatialLayers.reduce(
    ([staticLayers, dynamicLayers], layer) => {
      return isSpatialLayerDynamic(layer)
        ? [
            staticLayers,
            [...dynamicLayers, filterNegativeSpatialObjects(layer)],
          ]
        : [[...staticLayers, layer], dynamicLayers];
    },
    [[], []],
  );
};

/**
 * Find the default floor for a CRG spatial object's floors
 * If there is no `default_floor`, return the first floor in the list
 * @param {Array} floors - List of floors
 */
export const findCrgDefaultFloor = floors => {
  return floors.find(floor => floor.default_floor) || floors[0];
};

/**
 * Function to check if a spatial object has the `floors` attribute to determine
 * whether or not to render the CRG expanded map card.
 * @param {Object} objectData - Spatial object data
 */
export const shouldRenderExpandedMapCard = objectData => {
  return !!objectData?.attributes?.floors?.length;
};

/**
 * Check whether a location is within a polygon
 * checkWithinPolygon.feature() returns -1 when the coordinates are not in the
 * polygon and returns the polygon object when the coordinates are
 * @param {Object} polygon - polygon
 * @param {Array} coordinates - [lng, lat]
 */
export const isLocationInPolygon = (polygon, coordinates) => {
  return !!(checkWithinPolygon.feature(polygon, coordinates) !== -1);
};
